import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { ZoomIn, ZoomOut, ArrowLeft, Plus, Camera, Loader2, MapPin, FileText, Edit } from 'lucide-react';
import axiosInstance from '../lib/axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionContext';
import { getStatusColor, getStatusLabel, getPriorityColor, formatDate, formatDateBySetting, formatDateTimeBySetting, getRoleColor } from '../lib/utils';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { useTranslation } from 'react-i18next';

// Set up PDF.js worker - using local file for reliability
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const FloorViewer = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { projectId, floorId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const [floor, setFloor] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [summary, setSummary] = useState(null);
  const [users, setUsers] = useState([]);
  const [roleDisplayNames, setRoleDisplayNames] = useState({});
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scale, setScale] = useState('auto');
  const [calculatedScale, setCalculatedScale] = useState(1.0);
  const [initialScale, setInitialScale] = useState(1.0); // Store initial auto-calculated scale
  const [numPages, setNumPages] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [ticketDialogOpen, setTicketDialogOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketComments, setTicketComments] = useState([]);
  const [clickCoords, setClickCoords] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [editPositionDialogOpen, setEditPositionDialogOpen] = useState(false);
  const [locationViewDialogOpen, setLocationViewDialogOpen] = useState(false);
  const [locationViewScale, setLocationViewScale] = useState(1.5);
  const [newPosition, setNewPosition] = useState(null);
  const [editingTicket, setEditingTicket] = useState(null);
  const containerRef = useRef(null);
  const editPositionContainerRef = useRef(null);
  const locationViewContainerRef = useRef(null);
  const [containerDimensions, setContainerDimensions] = useState({ width: 0, height: 0 });
  const [dateFormat, setDateFormat] = useState('MM/DD/YYYY');
  
  // Status filter state
  const [statusFilters, setStatusFilters] = useState({
    new: true,
    in_progress: true,
    fixed: true,
    rejected: true,
    closed: true
  });

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    priority: '',
    assigned_to: [],
    deadline: '',
    photos: []
  });

  // Fetch functions defined BEFORE useEffect
  const fetchUsersForProject = useCallback(async (projectId) => {
    try {
      const response = await axiosInstance.get('/users');
      if (projectId) {
        const projectUsers = response.data.filter(u => 
          u.assigned_projects && u.assigned_projects.includes(projectId)
        );
        console.log(`Found ${projectUsers.length} users assigned to project ${projectId}`);
        setUsers(projectUsers);
      } else {
        setUsers(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  }, []);

  const fetchFloorData = useCallback(async () => {
    try {
      const [floorRes, ticketsRes, summaryRes] = await Promise.all([
        axiosInstance.get(`/floors/${floorId}`),
        axiosInstance.get(`/tickets?floor_id=${floorId}`),
        axiosInstance.get(`/floors/${floorId}/ticket-summary`)
      ]);
      setFloor(floorRes.data);
      setTickets(ticketsRes.data);
      setSummary(summaryRes.data);
      await fetchUsersForProject(floorRes.data.project_id);
    } catch (error) {
      toast.error('Failed to fetch floor data');
    } finally {
      setLoading(false);
    }
  }, [floorId, fetchUsersForProject]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  }, []);

  const fetchRoles = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/roles');
      const displayNameMap = {};
      response.data.forEach(role => {
        displayNameMap[role.name] = role.display_name;
      });
      setRoleDisplayNames(displayNameMap);
    } catch (error) {
      console.error('Failed to fetch roles:', error);
    }
  }, []);

  const fetchSiteSettings = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/settings');
      setDateFormat(response.data.date_format || 'MM/DD/YYYY');
    } catch (error) {
      console.error('Failed to fetch site settings:', error);
      // Use default if settings fetch fails
      setDateFormat('MM/DD/YYYY');
    }
  }, []);

  useEffect(() => {
    fetchFloorData();
    fetchCategories();
    fetchRoles();
    fetchSiteSettings();
  }, [floorId, location.pathname, fetchFloorData, fetchCategories, fetchRoles, fetchSiteSettings]); // Refetch when navigating back

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        setContainerDimensions({
          width: containerRef.current.offsetWidth,
          height: containerRef.current.offsetHeight
        });
      }
    };
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  const handlePdfLoadSuccess = ({ numPages, ...pdfInfo }) => {
    setNumPages(numPages);
    
    // Calculate scale to fit PDF width in container
    if (scale === 'auto' && containerRef.current) {
      const container = containerRef.current;
      const containerWidth = container.offsetWidth;
      
      // Get first page to calculate dimensions
      setTimeout(() => {
        const pdfPage = container.querySelector('.react-pdf__Page');
        if (pdfPage) {
          const pageWidth = pdfPage.offsetWidth;
          
          // Calculate scale to fit width with some padding (95% of container)
          const fitScale = (containerWidth * 0.95) / pageWidth;
          
          setCalculatedScale(fitScale);
          setScale(fitScale);
          setInitialScale(fitScale); // Store as minimum zoom level
        }
      }, 100);
    }
  };

  const handlePDFClick = (event) => {
    // Check permission first
    if (!hasPermission('create_ticket')) {
      toast.error(t('messages.error.failed'));
      return;
    }
    
    // Get the PDF page element (canvas or the page wrapper)
    const pdfPage = event.currentTarget.querySelector('.react-pdf__Page');
    if (!pdfPage) {
      console.error('PDF page not found');
      return;
    }
    
    const rect = pdfPage.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    
    // Only create ticket if click is within the PDF bounds
    if (x >= 0 && x <= 100 && y >= 0 && y <= 100) {
      setClickCoords({ x, y });
      setCreateDialogOpen(true);
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('floor_id', floorId);
      formDataToSend.append('title', formData.title);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('category', formData.category);
      formDataToSend.append('priority', formData.priority);
      formDataToSend.append('coordinate_x', clickCoords.x);
      formDataToSend.append('coordinate_y', clickCoords.y);
      if (formData.assigned_to.length > 0) formDataToSend.append('assigned_to', JSON.stringify(formData.assigned_to));
      if (formData.deadline) formDataToSend.append('deadline', formData.deadline);
      
      formData.photos.forEach((photo) => {
        formDataToSend.append('photos', photo);
      });

      // Delete Content-Type to let axios set it automatically for FormData
      const config = {
        headers: {
          'Content-Type': undefined
        }
      };
      await axiosInstance.post('/tickets', formDataToSend, config);

      toast.success(t('messages.success.created'));
      setCreateDialogOpen(false);
      setFormData({
        title: '',
        description: '',
        category: '',
        priority: '',
        assigned_to: [],
        deadline: '',
        photos: []
      });
      fetchFloorData();
    } catch (error) {
      console.error('Ticket creation error:', error);
      let errorMsg = t('messages.error.failed');
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        // Handle FastAPI validation errors (array of objects)
        if (Array.isArray(detail)) {
          errorMsg = detail.map(err => `${err.loc?.[1] || 'Field'}: ${err.msg}`).join(', ');
        } else if (typeof detail === 'string') {
          errorMsg = detail;
        }
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      toast.error(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  const toggleStatusFilter = (status) => {
    setStatusFilters(prev => ({ ...prev, [status]: !prev[status] }));
  };

  const handleMarkerClick = async (ticket) => {
    setSelectedTicket(ticket);
    setTicketDialogOpen(true);
    
    // Fetch comments for this ticket
    try {
      const response = await axiosInstance.get(`/tickets/${ticket.id}/comments`);
      setTicketComments(response.data);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    }
  };

  const handleEditPosition = (ticket) => {
    setEditingTicket(ticket);
    setNewPosition({ x: ticket.coordinate_x, y: ticket.coordinate_y });
    setEditPositionDialogOpen(true);
  };

  const handlePositionPDFClick = (e) => {
    if (!editPositionContainerRef.current) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    setNewPosition({ x, y });
  };

  const handleSaveNewPosition = async () => {
    if (!editingTicket || !newPosition) return;

    try {
      await axiosInstance.put(`/tickets/${editingTicket.id}`, {
        coordinate_x: newPosition.x,
        coordinate_y: newPosition.y,
      });

      toast.success(t('messages.success.updated'));
      
      // Close the position edit dialog
      setEditPositionDialogOpen(false);
      
      // Update selected ticket if it's the one being viewed
      if (selectedTicket && selectedTicket.id === editingTicket.id) {
        setSelectedTicket({
          ...selectedTicket,
          coordinate_x: newPosition.x,
          coordinate_y: newPosition.y,
        });
      }
      
      // Refresh ticket data to update markers on PDF
      await fetchFloorData();
      
      // Clean up
      setEditingTicket(null);
      setNewPosition(null);
    } catch (error) {
      console.error('Failed to update position:', error);
      toast.error(t('messages.error.failed'));
    }
  };

  const handleLocationViewPdfLoad = () => {
    // Center the view on the ticket marker after PDF loads
    setTimeout(() => {
      if (locationViewContainerRef.current && selectedTicket) {
        const container = locationViewContainerRef.current;
        const pdfElement = container.querySelector('.react-pdf__Page');
        
        if (pdfElement) {
          const pdfWidth = pdfElement.offsetWidth;
          const pdfHeight = pdfElement.offsetHeight;
          
          // Calculate marker position in pixels
          const markerX = (selectedTicket.coordinate_x / 100) * pdfWidth;
          const markerY = (selectedTicket.coordinate_y / 100) * pdfHeight;
          
          // Center the marker in the viewport
          const containerWidth = container.offsetWidth;
          const containerHeight = container.offsetHeight;
          
          container.scrollLeft = markerX - (containerWidth / 2);
          container.scrollTop = markerY - (containerHeight / 2);
        }
      }
    }, 100);
  };

  const filteredTickets = tickets.filter(ticket => statusFilters[ticket.status]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Convert /uploads/pdfs/filename to /api/files/pdfs/filename
  const pdfUrl = floor?.pdf_path ? floor.pdf_path.replace('/uploads/', '/api/files/') : null;

  return (
    <div className="space-y-6" data-testid="floor-viewer-page">
      {/* Back Button */}
      <Link
        to={`/projects/${projectId}`}
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4"
      >
        <ArrowLeft className="w-4 h-4" />
        {t('floorViewer.backToProject')}
      </Link>

      {/* Header with Summary and Filters */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        {/* Floor Name */}
        <div>
          <h1 className="text-3xl font-black tracking-tight">{floor?.name}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t('floorViewer.clickToCreateInstruction')}</p>
        </div>

        {/* Ticket Summary - Compact */}
        <Card data-testid="ticket-summary-panel" className="lg:col-span-1">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">{t('floorViewer.ticketSummary')}</CardTitle>
          </CardHeader>
          <CardContent className="pt-2">
            <div className="grid grid-cols-6 gap-2">
              <div className="text-center">
                <div className="text-lg font-black text-red-500">{summary?.new || 0}</div>
                <div className="text-[10px] text-muted-foreground uppercase font-bold">{t('floorViewer.statusNew')}</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-black text-yellow-500">{summary?.in_progress || 0}</div>
                <div className="text-[10px] text-muted-foreground uppercase font-bold">{t('floorViewer.statusProgress')}</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-black text-green-500">{summary?.fixed || 0}</div>
                <div className="text-[10px] text-muted-foreground uppercase font-bold">{t('floorViewer.statusFixed')}</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-black text-orange-500">{summary?.rejected || 0}</div>
                <div className="text-[10px] text-muted-foreground uppercase font-bold">{t('floorViewer.statusReject')}</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-black text-gray-500">{summary?.closed || 0}</div>
                <div className="text-[10px] text-muted-foreground uppercase font-bold">{t('floorViewer.statusClosed')}</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-black">{summary?.total || 0}</div>
                <div className="text-[10px] text-muted-foreground uppercase font-bold">{t('floorViewer.statusTotal')}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Status Filters - Compact */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">{t('floorViewer.filterByStatus')}</CardTitle>
          </CardHeader>
          <CardContent className="pt-2">
            <div className="flex flex-wrap gap-2">
              {Object.keys(statusFilters).map((status) => {
                const statusLabelMap = {
                  'new': t('tickets.statuses.new'),
                  'in_progress': t('tickets.statuses.inProgress'),
                  'fixed': t('tickets.statuses.fixed'),
                  'rejected': t('tickets.statuses.rejected'),
                  'closed': t('tickets.statuses.closed')
                };
                return (
                  <div key={status} className="flex items-center space-x-1">
                    <Switch
                      id={`filter-${status}`}
                      checked={statusFilters[status]}
                      onCheckedChange={() => toggleStatusFilter(status)}
                      data-testid={`filter-${status}`}
                      className="scale-75"
                    />
                    <Label htmlFor={`filter-${status}`} className="cursor-pointer">
                      <Badge className={`${getStatusColor(status)} text-xs`}>
                        {statusLabelMap[status] || status}
                      </Badge>
                    </Label>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* PDF Viewer with Markers */}
      <Card className="relative">
        <CardContent className="p-0 relative">
          {/* Zoom Controls - Fixed at Bottom-Right, moved up to avoid overlap */}
          <div className="fixed bottom-24 right-8 flex flex-col items-center gap-2 bg-background/95 backdrop-blur-sm border-2 border-border rounded-lg p-2 shadow-xl" style={{ zIndex: 40 }}>
            <Button 
              variant="outline" 
              size="icon" 
              onClick={() => setScale(Math.min(2, scale + 0.1))} 
              data-testid="zoom-in-button"
              className="h-12 w-12"
            >
              <ZoomIn className="w-8 h-8 text-green-600" />
            </Button>
            <span className="text-sm font-mono font-bold rotate-0 w-12 text-center">{Math.round((scale === 'auto' ? calculatedScale : scale) * 100)}%</span>
            <Button 
              variant="outline" 
              size="icon" 
              onClick={() => setScale(Math.max(initialScale, scale - 0.1))} 
              data-testid="zoom-out-button"
              className="h-12 w-12"
            >
              <ZoomOut className="w-8 h-8 text-red-600" />
            </Button>
          </div>

          <div
            ref={containerRef}
            className="relative overflow-auto bg-muted/30"
            style={{ minHeight: '80vh' }}
            data-testid="pdf-container"
          >
            <div className="relative inline-block" onClick={handlePDFClick} style={{ cursor: 'crosshair' }}>
              <Document
                file={pdfUrl}
                onLoadSuccess={handlePdfLoadSuccess}
                onLoadError={(error) => {
                  console.error('PDF load error:', error);
                  toast.error(`Failed to load PDF: ${error.message}`);
                }}
                loading={
                  <div className="flex items-center justify-center h-96">
                    <Loader2 className="w-12 h-12 animate-spin text-primary" />
                  </div>
                }
                error={
                  <div className="flex flex-col items-center justify-center h-96 p-6 text-center">
                    <FileText className="w-16 h-16 text-muted-foreground mb-4" />
                    <p className="text-lg font-semibold mb-2">Failed to load PDF file</p>
                    <p className="text-sm text-muted-foreground mb-4">
                      The PDF file could not be loaded. Please try refreshing the page.
                    </p>
                    <p className="text-xs text-muted-foreground font-mono">
                      File: {floor?.pdf_path}
                    </p>
                  </div>
                }
              >
                <Page
                  pageNumber={1}
                  scale={scale === 'auto' ? 1.0 : scale}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              </Document>
              
              {/* Ticket Markers - Positioned relative to the parent div */}
              {filteredTickets.map((ticket) => {
                const isPriority = ticket.priority === 'urgent';
                return (
                  <div
                    key={ticket.id}
                    className="ticket-marker absolute group pointer-events-auto cursor-pointer"
                    style={{
                      left: `${ticket.coordinate_x}%`,
                      top: `${ticket.coordinate_y}%`,
                      transform: 'translate(-50%, -50%)',
                      zIndex: 10
                    }}
                    data-testid={`ticket-marker-${ticket.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMarkerClick(ticket);
                    }}
                  >
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center shadow-lg border-2 border-white ${
                        getStatusColor(ticket.status)
                      } ${isPriority ? 'pulse-urgent' : ''}`}
                    >
                      <MapPin className="w-5 h-5 text-white" />
                    </div>
                    {/* Tooltip */}
                    <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-64 p-3 bg-card border border-border rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                      {ticket.ticket_code && (
                        <p className="font-mono text-xs text-primary mb-1">{ticket.ticket_code}</p>
                      )}
                      <p className="font-semibold text-sm mb-1">{ticket.title}</p>
                      <p className="text-xs text-muted-foreground mb-2 line-clamp-2">{ticket.description}</p>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" size="sm" className="capitalize">
                          {ticket.category}
                        </Badge>
                        <Badge className={getStatusColor(ticket.status)} size="sm">
                          {t(`tickets.statuses.${ticket.status === 'in_progress' ? 'inProgress' : ticket.status}`)}
                        </Badge>
                        <Badge variant="outline" size="sm" className="capitalize">
                          {t(`tickets.priorities.${ticket.priority}`)}
                        </Badge>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Create Ticket Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('tickets.createTicket')}</DialogTitle>
            <DialogDescription>
              {t('floorViewer.coordinateX')}: {clickCoords?.x.toFixed(1)}%, {t('floorViewer.coordinateY')}: {clickCoords?.y.toFixed(1)}%
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateTicket} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">{t('tickets.ticketTitle')} *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                placeholder={t('tickets.ticketTitle')}
                data-testid="ticket-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">{t('tickets.description')} *</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                rows={4}
                placeholder={t('tickets.description')}
                data-testid="ticket-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="category">{t('tickets.category')} *</Label>
                <Select onValueChange={(value) => setFormData({ ...formData, category: value })} required>
                  <SelectTrigger data-testid="ticket-category-select">
                    <SelectValue placeholder={t('tickets.category')} />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.length > 0 ? (
                      categories.map((category) => (
                        <SelectItem key={category.id} value={category.name}>
                          {category.name}
                        </SelectItem>
                      ))
                    ) : (
                      <>
                        <SelectItem value="structural">Structural</SelectItem>
                        <SelectItem value="electrical">Electrical</SelectItem>
                        <SelectItem value="plumbing">Plumbing</SelectItem>
                        <SelectItem value="fire_safety">Fire Safety</SelectItem>
                        <SelectItem value="hvac">HVAC</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="priority">{t('tickets.priority')} *</Label>
                <Select onValueChange={(value) => setFormData({ ...formData, priority: value })} required>
                  <SelectTrigger data-testid="ticket-priority-select">
                    <SelectValue placeholder={t('tickets.priority')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="urgent">{t('tickets.priorities.urgent')}</SelectItem>
                    <SelectItem value="high">{t('tickets.priorities.high')}</SelectItem>
                    <SelectItem value="medium">{t('tickets.priorities.medium')}</SelectItem>
                    <SelectItem value="low">{t('tickets.priorities.low')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>{t('tickets.assignedTo')}</Label>
                <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                  {users.map((user) => (
                    <label key={user.id} className="flex items-center gap-2 cursor-pointer hover:bg-muted/50 p-2 rounded">
                      <input
                        type="checkbox"
                        checked={formData.assigned_to.includes(user.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({ ...formData, assigned_to: [...formData.assigned_to, user.id] });
                          } else {
                            setFormData({ ...formData, assigned_to: formData.assigned_to.filter(id => id !== user.id) });
                          }
                        }}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">{user.name}</span>
                      <Badge className={`${getRoleColor(user.role)} text-xs ml-auto capitalize border-0`}>
                        {roleDisplayNames[user.role] || user.role.replace('_', ' ')}
                      </Badge>
                    </label>
                  ))}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="deadline">{t('tickets.deadline')}</Label>
                <Input
                  id="deadline"
                  type="date"
                  value={formData.deadline}
                  onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                  data-testid="ticket-deadline-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="photos">{t('tickets.photos')}</Label>
              <Input
                id="photos"
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => setFormData({ ...formData, photos: Array.from(e.target.files) })}
                data-testid="ticket-photos-input"
              />
              <p className="text-xs text-muted-foreground">{t('tickets.uploadPhotos')}</p>
            </div>
            <Button 
              type="submit" 
              className="w-full" 
              disabled={uploading || !formData.category || !formData.priority} 
              data-testid="submit-ticket-button"
            >
              {uploading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('common.loading')}
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  {t('tickets.createTicket')}
                </>
              )}
            </Button>
            {(!formData.category || !formData.priority) && (
              <p className="text-sm text-destructive text-center">
                {t('floorViewer.selectCategoryPriority')}
              </p>
            )}
          </form>
        </DialogContent>
      </Dialog>

      {/* Ticket Detail Dialog */}
      <Dialog open={ticketDialogOpen} onOpenChange={setTicketDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedTicket && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <DialogTitle className="text-2xl">{selectedTicket.title}</DialogTitle>
                      {selectedTicket.ticket_code && (
                        <code className="text-sm font-mono px-2 py-1 bg-muted rounded">
                          {selectedTicket.ticket_code}
                        </code>
                      )}
                    </div>
                    <DialogDescription className="mt-2">
                      <code className="text-xs font-mono">ID: {selectedTicket.id}</code>
                    </DialogDescription>
                  </div>
                  <Badge className={`${getStatusColor(selectedTicket.status)} text-lg px-3 py-1`}>
                    {t(`tickets.statuses.${selectedTicket.status === 'in_progress' ? 'inProgress' : selectedTicket.status}`)}
                  </Badge>
                </div>
              </DialogHeader>

              <div className="space-y-6 mt-4">
                {/* Description */}
                <div>
                  <h3 className="font-semibold mb-2">{t('floorViewer.description')}</h3>
                  <p className="text-muted-foreground whitespace-pre-wrap">{selectedTicket.description}</p>
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">{t('floorViewer.priority')}</p>
                    <Badge variant="outline" className={`mt-1 capitalize ${getPriorityColor(selectedTicket.priority)}`}>
                      {selectedTicket.priority}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">{t('floorViewer.category')}</p>
                    <p className="text-sm font-medium mt-1 capitalize">{selectedTicket.category}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">{t('floorViewer.location')}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <button
                        onClick={() => {
                          setLocationViewDialogOpen(true);
                          setLocationViewScale(1.5);
                        }}
                        className="text-sm font-medium text-primary hover:underline cursor-pointer flex items-center gap-1"
                      >
                        <MapPin className="w-3 h-3" />
                        ({selectedTicket.coordinate_x.toFixed(1)}%, {selectedTicket.coordinate_y.toFixed(1)}%)
                      </button>
                      {(user?.role === 'admin' || user?.role === 'inspector' || selectedTicket.created_by === user?.id) && 
                       selectedTicket.status !== 'closed' && (
                        <button
                          onClick={() => handleEditPosition(selectedTicket)}
                          className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1"
                          title={t('floorViewer.editPosition')}
                        >
                          <Edit className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">{t('floorViewer.created')}</p>
                    <p className="text-sm font-medium mt-1">{formatDateTimeBySetting(selectedTicket.created_at, dateFormat)}</p>
                  </div>
                  {selectedTicket.deadline && (
                    <div>
                      <p className="text-xs text-muted-foreground uppercase">{t('floorViewer.deadline')}</p>
                      <p className="text-sm font-medium mt-1">{formatDateBySetting(selectedTicket.deadline, dateFormat)}</p>
                    </div>
                  )}
                </div>

                {/* Photos */}
                {selectedTicket.photos && selectedTicket.photos.length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-3">{t('floorViewer.photos')} ({selectedTicket.photos.length})</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {selectedTicket.photos.map((photo, index) => (
                        <img
                          key={index}
                          src={photo.replace('/uploads/', '/api/files/')}
                          alt={`Ticket photo ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg border border-border cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => window.open(photo.replace('/uploads/', '/api/files/'), '_blank')}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Comments */}
                {ticketComments.length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-3">{t('floorViewer.comments')} ({ticketComments.length})</h3>
                    <div className="space-y-4">
                      {ticketComments.map((comment) => (
                        <div key={comment.id} className="p-3 bg-muted/30 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                              <span className="text-xs font-bold text-primary-foreground">
                                {comment.user_name?.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <span className="font-semibold text-sm">{comment.user_name}</span>
                            <span className="text-xs text-muted-foreground">{formatDate(comment.created_at)}</span>
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">{comment.content}</p>
                          {comment.photos && comment.photos.length > 0 && (
                            <div className="grid grid-cols-3 gap-2 mt-2">
                              {comment.photos.map((photo, index) => (
                                <img
                                  key={index}
                                  src={photo.replace('/uploads/', '/api/files/')}
                                  alt={`Comment photo ${index + 1}`}
                                  className="w-full h-20 object-cover rounded border border-border"
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-2 pt-4 border-t">
                  {(user?.role === 'admin' || user?.role === 'inspector' || selectedTicket.created_by === user?.id) && 
                   selectedTicket.status !== 'closed' && (
                    <Button
                      variant="default"
                      className="gap-2"
                      onClick={() => {
                        setTicketDialogOpen(false);
                        navigate(`/tickets/${selectedTicket.id}`, {
                          state: { 
                            returnTo: `/projects/${projectId}/floors/${floorId}`,
                            returnLabel: floor?.name 
                          }
                        });
                      }}
                    >
                      <Edit className="w-4 h-4" />
                      {t('floorViewer.editTicket')}
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      setTicketDialogOpen(false);
                      navigate(`/tickets/${selectedTicket.id}`, {
                        state: { 
                          returnTo: `/projects/${projectId}/floors/${floorId}`,
                          returnLabel: floor?.name 
                        }
                      });
                    }}
                  >
                    {t('floorViewer.viewFullDetails')}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => setTicketDialogOpen(false)}
                  >
                    {t('floorViewer.close')}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Position Dialog */}
      <Dialog open={editPositionDialogOpen} onOpenChange={setEditPositionDialogOpen}>
        <DialogContent className="max-w-6xl h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b">
            <DialogTitle>{t('floorViewer.editTicketPosition')}</DialogTitle>
            <DialogDescription>
              {t('floorViewer.clickToSetNewLocation')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 flex flex-col overflow-hidden px-6">
            {newPosition && (
              <div className="p-3 bg-muted/50 rounded-lg mb-3 flex-shrink-0">
                <p className="text-sm">
                  <MapPin className="w-4 h-4 inline mr-2" />
                  {t('floorViewer.newPosition')} ({newPosition.x.toFixed(1)}%, {newPosition.y.toFixed(1)}%)
                </p>
              </div>
            )}
            
            {/* PDF Viewer with Zoom Controls */}
            <div className="relative flex-1 border rounded-lg overflow-auto bg-muted/30 mb-4">
              {/* Zoom Controls */}
              <div className="absolute top-4 right-4 z-50 flex flex-col items-center gap-2 bg-background/95 backdrop-blur-sm border-2 border-border rounded-lg p-2 shadow-xl">
                <Button 
                  variant="outline" 
                  size="icon" 
                  onClick={() => setScale(Math.min(2, scale + 0.1))} 
                  className="h-10 w-10"
                >
                  <ZoomIn className="w-5 h-5" />
                </Button>
                <span className="text-xs font-mono font-bold w-10 text-center">{Math.round(scale * 100)}%</span>
                <Button 
                  variant="outline" 
                  size="icon" 
                  onClick={() => setScale(Math.max(0.5, scale - 0.1))} 
                  className="h-10 w-10"
                >
                  <ZoomOut className="w-5 h-5" />
                </Button>
              </div>

              <div 
                ref={editPositionContainerRef}
                className="relative inline-block cursor-crosshair"
                onClick={handlePositionPDFClick}
              >
                <Document
                  file={pdfUrl}
                  onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                  loading={
                    <div className="flex items-center justify-center h-96">
                      <Loader2 className="w-12 h-12 animate-spin text-primary" />
                    </div>
                  }
                >
                  <Page
                    pageNumber={1}
                    scale={scale}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                </Document>

                {/* Show new position marker */}
                {newPosition && (
                  <div
                    style={{
                      position: 'absolute',
                      left: `${newPosition.x}%`,
                      top: `${newPosition.y}%`,
                      transform: 'translate(-50%, -50%)',
                      zIndex: 10,
                    }}
                  >
                    <div className="relative">
                      <MapPin className="w-8 h-8 text-blue-600 drop-shadow-lg" fill="currentColor" />
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-600 rounded-full animate-ping" />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons - Always Visible */}
            <div className="flex gap-2 justify-end py-4 border-t flex-shrink-0">
              <Button
                variant="outline"
                onClick={() => {
                  setEditPositionDialogOpen(false);
                  setEditingTicket(null);
                  setNewPosition(null);
                }}
              >
                {t('floorViewer.cancel')}
              </Button>
              <Button
                onClick={handleSaveNewPosition}
                disabled={!newPosition}
              >
                {t('floorViewer.saveNewPosition')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Location Viewer Dialog */}
      <Dialog open={locationViewDialogOpen} onOpenChange={setLocationViewDialogOpen}>
        <DialogContent className="max-w-6xl h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b">
            <DialogTitle>{t('floorViewer.ticketLocationOnFloorPlan')}</DialogTitle>
            <DialogDescription>
              {floor?.name} - {t('tickets.ticketPosition')} ({selectedTicket?.coordinate_x.toFixed(1)}%, {selectedTicket?.coordinate_y.toFixed(1)}%)
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden px-6 pb-6 relative">
            {/* Zoom Controls */}
            <div className="absolute top-4 right-10 z-50 flex flex-col items-center gap-2 bg-background/95 backdrop-blur-sm border-2 border-border rounded-lg p-2 shadow-xl">
              <Button 
                variant="outline" 
                size="icon" 
                onClick={() => setLocationViewScale(Math.min(2, locationViewScale + 0.1))} 
                className="h-10 w-10"
              >
                <ZoomIn className="w-5 h-5" />
              </Button>
              <span className="text-xs font-mono font-bold w-10 text-center">{Math.round(locationViewScale * 100)}%</span>
              <Button 
                variant="outline" 
                size="icon" 
                onClick={() => setLocationViewScale(Math.max(0.5, locationViewScale - 0.1))} 
                className="h-10 w-10"
              >
                <ZoomOut className="w-5 h-5" />
              </Button>
            </div>

            <div ref={locationViewContainerRef} className="w-full h-full overflow-auto bg-muted/30 rounded-lg relative">
              <div className="relative inline-block">
                {floor?.pdf_path && (
                  <Document
                    file={floor.pdf_path.replace('/uploads/', '/api/files/')}
                    onLoadSuccess={handleLocationViewPdfLoad}
                    loading={
                      <div className="flex items-center justify-center h-96">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                      </div>
                    }
                  >
                    <Page
                      pageNumber={1}
                      scale={locationViewScale}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />
                  </Document>
                )}
                
                {/* Ticket Marker */}
                {selectedTicket && (
                  <div
                    className="absolute pointer-events-none"
                    style={{
                      left: `${selectedTicket.coordinate_x}%`,
                      top: `${selectedTicket.coordinate_y}%`,
                      transform: 'translate(-50%, -50%)',
                      zIndex: 10
                    }}
                  >
                    <div className="relative">
                      <div
                        className={`w-12 h-12 rounded-full flex items-center justify-center shadow-xl border-4 border-white ${getStatusColor(selectedTicket.status)} animate-pulse`}
                      >
                        <MapPin className="w-8 h-8 text-white" />
                      </div>
                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full animate-ping" />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FloorViewer;